// src/pages/WorkspaceDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints } from '../services/api';
import '../styles/pages/Page.css';
import '../styles/pages/Analytics.css';
import Mascot from '../components/Mascot';
import { withErrorBoundary } from '../components/ErrorBoundary';
import { getFriendlyError } from '../components/errorUtils';



// ── Main Component ─────────────────────────────────────────────────────────────
const WorkspaceDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [guides, setGuides] = useState([]);          // always an array now
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    if (user !== null && user.plan !== 'teams') { navigate('/my-guides'); return; }

    const load = async () => {
      try {
        const [wsRes, guidesRes] = await Promise.all([
          fetch(endpoints.workspace.base, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(endpoints.guides.base, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (wsRes.ok) setWorkspace(await wsRes.json());

        if (guidesRes.ok) {
          const guidesData = await guidesRes.json();
          // API returns paginated { items: [], has_more: bool } — extract the array
          setGuides(Array.isArray(guidesData) ? guidesData : (guidesData.items ?? []));
        }
      } catch (e) {
        setError(getFriendlyError('Failed to load workspace. Please refresh and try again.', 'Workspace Dashboard'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, user,navigate]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteStatus('');
    try {
      const res = await fetch(endpoints.workspace.invite, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to invite');
      setInviteStatus(data.message);
      setInviteEmail('');
      const wsRes = await fetch(endpoints.workspace.base, { headers: { Authorization: `Bearer ${token}` } });
      if (wsRes.ok) setWorkspace(await wsRes.json());
    } catch (err) {
      setInviteStatus(`Error: ${err.message}`);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the workspace?')) return;
    try {
      const res = await fetch(endpoints.workspace.removeMember(userId), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const wsRes = await fetch(endpoints.workspace.base, { headers: { Authorization: `Bearer ${token}` } });
      if (wsRes.ok) setWorkspace(await wsRes.json());
    } catch (err) {
      setError(getFriendlyError(err.message, 'Workspace Dashboard'));
    }
  };

  if (loading) return (
    <div className="an-page" aria-busy="true" aria-label="Loading workspace">
      <div className="m-page-head">
        <div>
          <span className="an-skel" style={{ height: 11, width: 90, marginBottom: 14 }} />
          <span className="an-skel" style={{ height: 44, width: 280 }} />
        </div>
        <span className="an-skel" style={{ height: 32, width: 90 }} />
      </div>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--border-color)' }}>
          <span className="an-skel" style={{ width: 32, height: 32, borderRadius: 9 }} />
          <span className="an-skel" style={{ flex: 1, maxWidth: 260, height: 12 }} />
          <span className="an-skel" style={{ width: 60, height: 18 }} />
        </div>
      ))}
    </div>
  );

  if (!workspace) return (
    <div className="an-page">
      <div className="m-sheet an-empty">
        <Mascot size={48} />
        <div>
          <h2>No workspace found</h2>
          <p>{error || 'You need a Teams plan to access this page.'}</p>
          <a href="/#pricing" className="lp-btn sm">View plans</a>
        </div>
      </div>
    </div>
  );

  const seatsUsed = workspace.members?.length || 0;
  const seatsTotal = (workspace.seats_included || 5) + (workspace.extra_seats || 0);
  const isAdmin = workspace.members?.find(m => m.user_id === user?.id)?.role === 'admin';
  const atSeatLimit = seatsUsed >= seatsTotal;
  const workspaceGuides = guides.filter(g => g.workspace_id === workspace.id); // now safe
  const inviteFailed = inviteStatus.startsWith('Error');

  return (
    <div className="an-page">
      {/* Header */}
      <header className="m-page-head">
        <div>
          <span className="m-eyebrow" style={{ display: 'block', marginBottom: 12 }}>Workspace</span>
          <h1>{workspace.name}</h1>
        </div>
        <div className={`ws-seats${atSeatLimit ? ' is-full' : ''}`}>
          <div className="ws-seats__count">{seatsUsed} / {seatsTotal}</div>
          <div className="ws-seats__meter" aria-hidden="true">
            {Array.from({ length: Math.min(seatsTotal, 20) }).map((_, i) => (
              <i key={i} className={i < seatsUsed ? 'on' : ''} />
            ))}
          </div>
          <div className="m-eyebrow">Seats used</div>
        </div>
      </header>

      {error && <div className="an-error" role="alert"><p>{error}</p></div>}

      {/* Members Table */}
      <section className="an-section" style={{ marginTop: 40 }}>
        <div className="an-section__head"><h2>Team members</h2></div>
        <div className="an-tablewrap">
          <table className="an-table">
            <thead>
              <tr>
                {['Member', 'Role', 'Joined', isAdmin ? 'Actions' : ''].filter(Boolean).map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workspace.members?.map((member) => (
                <tr key={member.id}>
                  <td>
                    <div className="ws-member">
                      <span className="ws-avatar" aria-hidden="true">{member.email?.[0]?.toUpperCase() || '?'}</span>
                      <span>{member.email}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`m-badge ${member.role === 'admin' ? 'm-badge--shared' : 'm-badge--private'}`}>
                      {member.role === 'admin' ? 'Admin' : 'Member'}
                    </span>
                  </td>
                  <td className="m-mono">
                    {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '—'}
                  </td>
                  {isAdmin && (
                    <td>
                      {member.user_id !== user?.id && (
                        <button className="ws-remove" onClick={() => handleRemoveMember(member.user_id)}>Remove</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Invite Section */}
      {isAdmin && (
        <section className="an-section">
          <div className="an-section__head">
            <h2>Invite a member</h2>
            {atSeatLimit && <p style={{ color: 'var(--primary-accent)' }}>Seat limit reached ({seatsTotal} seats). Contact us to add more seats.</p>}
          </div>
          <form onSubmit={handleInvite} className="ws-invite">
            <label htmlFor="ws-invite-email" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Teammate email</label>
            <input
              id="ws-invite-email" className="ws-input" type="email" value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)} placeholder="teammate@company.com" required disabled={atSeatLimit}
            />
            <button type="submit" className="lp-btn" disabled={atSeatLimit}>Send invite</button>
          </form>
          {inviteStatus && (
            <p className={`ws-msg${inviteFailed ? ' ws-msg--error' : ''}`} role={inviteFailed ? 'alert' : 'status'}>
              {inviteStatus}
            </p>
          )}
        </section>
      )}

      {/* Workspace Guides */}
      <section className="an-section">
        <div className="an-section__head">
          <h2>Workspace guides <span className="m-mono" style={{ fontSize: 14, color: 'var(--text-dim)', fontWeight: 400 }}>{String(workspaceGuides.length).padStart(2, '0')}</span></h2>
        </div>
        {workspaceGuides.length === 0 ? (
          <p className="an-note">No workspace guides yet. Guides created by team members will appear here.</p>
        ) : (
          <ul className="ws-guides">
            {workspaceGuides.map(guide => (
              <li key={guide.id}>
                <div>
                  <h3>{guide.name}</h3>
                  <p>
                    <code className="ws-code">{guide.shortcut}</code>
                    {guide.description && ` · ${guide.description}`}
                  </p>
                </div>
                <span className={`m-badge ${guide.is_public ? 'm-badge--public' : 'm-badge--private'}`}>
                  {guide.is_public ? 'Public' : 'Private'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default withErrorBoundary(WorkspaceDashboard, 'WorkspaceDashboard');